import type { ReactNode } from "react";

// Imports a custom hook for accessing global theme and preference states (e.g., dark/light mode)
import { usePreferences } from "#src/hooks/use-preferences";
// Ant Design components and hooks
import { ConfigProvider, theme } from "antd";
import { useContext } from "react";
// JSS ThemeProvider from the 'react-jss' library
import { ThemeProvider } from "react-jss";

/**
 * Defines the props for the JSS Theme Provider component.
 */
export interface JSSThemeProviderProps {
	/**
	 * Children components, which will receive the JSS theme tokens via the JSS context.
	 */
	children: ReactNode
}

// Destructures the useToken hook from Ant Design's theme module
const { useToken } = theme;

/**
 * JSSThemeProvider Component
 *
 * This component acts as a bridge, retrieving Ant Design's active design tokens
 * and global theme state, and passing them down to all descendant components
 * that utilize the React-JSS styling library.
 *
 * @param {JSSThemeProviderProps} props Component properties
 * @returns {JSX.Element} The JSX element wrapping the children with the JSS ThemeProvider
 */
export function JSSThemeProvider({ children }: JSSThemeProviderProps) {
	// 1. Get Ant Design Context
	// Accesses the global context from Ant Design's ConfigProvider (if present).
	const antdContext = useContext(ConfigProvider.ConfigContext);

	// Retrieves the configured prefix for Ant Design's CSS classes (e.g., 'ant').
	// This is included in the JSS theme for consistency.
	const prefixCls = antdContext.getPrefixCls();

	// 2. Get Ant Design Tokens
	// Uses Ant Design's hook to retrieve the current set of design tokens (color, spacing, border-radius, etc.).
	// These tokens reflect the currently active Ant Design theme (including dark mode adjustments).
	const { token } = useToken();

	// 3. Get Global Theme State
	// Uses a custom hook to retrieve the application's current theme state (e.g., color scheme, isDark status).
	const { theme, isDark, isLight } = usePreferences();

	// 4. Provide Theme to JSS
	return (
		// The react-jss ThemeProvider passes the assembled theme object down through React Context.
		// JSS-styled components can then consume this 'theme' object.
		<ThemeProvider
			theme={{
				token, // Ant Design's design tokens (the core styling data)
				theme, // The application's current theme name/ID
				isDark, // Boolean state: Is the application in Dark Mode?
				isLight, // Boolean state: Is the application in Light Mode?
				prefixCls, // Ant Design's configured class prefix
			}}
		>
			{children}
		</ThemeProvider>
	);
}
